import React from "react";
import { Descriptions, Divider, Alert, Row, Col, Statistic } from "antd";
import {
  CheckCircleOutlined,
  WarningOutlined,
  ClockCircleOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import { AllocationBreakdown } from "../helpers/shared-interfaces";
import moment from "moment";
import { TreasuryBillRedemptionBreakdown } from "./TreasuryBillRedemptionBreakdown";

type AllocationBreakdownData = AllocationBreakdown["data"];

interface CalculatedBreakdownDisplayProps {
  breakdown: AllocationBreakdown | AllocationBreakdownData;
  showExplanations?: boolean; // Control whether to show alerts and explanatory text
}

export const CalculatedBreakdownDisplay: React.FC<
  CalculatedBreakdownDisplayProps
> = ({ breakdown, showExplanations = true }) => {
  if (!breakdown) {
    return (
      <Alert
        message="Invalid Data"
        description="Unable to display allocation breakdown. Please try calculating again."
        type="error"
        showIcon
        style={{ marginTop: 24 }}
      />
    );
  }

  const data: AllocationBreakdownData =
    "data" in breakdown ? breakdown.data : breakdown;

  const isEarly = data.timing === "early";
  const redemption = data.redemption_data;
  const matured = data.matured_redemption_data;
  const expected = data.expected_initial_return_on_maturity_data;

  if (!redemption || !matured || !expected) {
    return (
      <Alert
        message="Incomplete Data"
        description="Some breakdown data is missing. Please try calculating again."
        type="warning"
        showIcon
        style={{ marginTop: 24 }}
      />
    );
  }

  const parseValue = (val: any): number => {
    if (typeof val === "object" && val !== null && "parsedValue" in val) {
      return val.parsedValue;
    }
    return parseFloat(val) || 0;
  };

  if (!showExplanations) {
    return (
      <div>
        <Descriptions
          size="small"
          column={{ xs: 1, sm: 3}}
          title="Calculation Results"
        >
          <Descriptions.Item label="Initial Investment">
            GHS {parseValue(data.initial_investment_amount).toFixed(2)}
          </Descriptions.Item>
          <Descriptions.Item label="Current Redeemable">
            GHS {parseValue(data.redeemable_amount).toFixed(2)}
          </Descriptions.Item>
          <Descriptions.Item label="Interest Rate">
            {parseValue(data.interest_rate).toFixed(2)}% p.a.
          </Descriptions.Item>
          <Descriptions.Item label="Days Invested">
            {data.days_invested} days
          </Descriptions.Item>
          <Descriptions.Item label="Maturity Date">
            {moment(data.maturity_date).format("DD MMM YYYY")}
          </Descriptions.Item>
          <Descriptions.Item label="Redemption Type">
            {data.redemption_type === "full" ? "Full" : "Partial"}
          </Descriptions.Item>
        </Descriptions>

        <Descriptions
          size="small"
          column={{ xs: 1, sm: 3}}
          style={{ marginTop: 16 }}
          title="Expected Return at Maturity"
        >
          <Descriptions.Item label="Expected Interest">
            GHS {parseValue(expected.expected_interest).toFixed(2)}
          </Descriptions.Item>
          <Descriptions.Item label="Expected Total">
            <span style={{ fontWeight: 600, color: "#1890ff" }}>
              GHS {parseValue(expected.expected_total_amount).toFixed(2)}
            </span>
          </Descriptions.Item>
        </Descriptions>

        {data.treasury_bill ? (
          <div style={{ marginTop: 16 }}>
            <TreasuryBillRedemptionBreakdown data={data} showExplanations={false} />
          </div>
        ) : (
        <Descriptions
          size="small"
          column={{ xs: 1, sm: 3}}
          style={{ marginTop: 16 }}
          title="Redemption Calculation"
        >
          <Descriptions.Item label="Redemption Amount">
            GHS {parseValue(redemption.redemption_amount).toFixed(2)}
          </Descriptions.Item>
          <Descriptions.Item label="Interest Earned">
            GHS {parseValue(redemption.redemption_interest_earned).toFixed(2)}
          </Descriptions.Item>
          {isEarly && parseValue(redemption.redemption_interest_penalty) > 0 && (
            <Descriptions.Item label="Penalty (20%)">
              <span style={{ color: "#cf1322" }}>
                GHS{" "}
                {parseValue(redemption.redemption_interest_penalty).toFixed(2)}
              </span>
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Total Redemption">
            <span style={{ fontWeight: 600, color: "#52c41a" }}>
              GHS {parseValue(redemption.total_redemption_amount).toFixed(2)}
            </span>
          </Descriptions.Item>
          {isEarly && parseValue(redemption.loss) > 0 && (
            <Descriptions.Item label="Loss vs Maturity">
              <span style={{ color: "#fa8c16" }}>
                GHS {parseValue(redemption.loss).toFixed(2)}
              </span>
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Rediscount Date">
            {moment(redemption.rediscount_date).format("DD MMM YYYY")}
          </Descriptions.Item>
        </Descriptions>
        )}
      </div>
    );
  }

  return (
    <div style={{ marginTop: 24 }}>
      <Divider orientation="left">
        <CheckCircleOutlined style={{ marginRight: 8, color: "#52c41a" }} />
        Calculation Results
      </Divider>

      {/* Investment Overview */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Statistic
            title="Initial Investment"
            value={parseValue(data.initial_investment_amount)}
            precision={2}
            prefix="GHS"
            valueStyle={{ fontSize: "16px", fontWeight: 600 }}
          />
        </Col>

        <Col xs={12} sm={6}>
          <Statistic
            title="Current Redeemable"
            value={parseValue(data.redeemable_amount)}
            precision={2}
            prefix="GHS"
            valueStyle={{ fontSize: "16px", fontWeight: 600 }}
          />
        </Col>

        <Col xs={12} sm={6}>
          <Statistic
            title="Days Invested"
            value={data.days_invested}
            suffix="days"
            valueStyle={{ fontSize: "16px", fontWeight: 600 }}
          />
        </Col>

        <Col xs={12} sm={6}>
          <Statistic
            title="Interest Rate"
            value={parseValue(data.interest_rate)}
            suffix="% p.a."
            precision={2}
            valueStyle={{ fontSize: "16px", fontWeight: 600 }}
          />
        </Col>
      </Row>

      <Alert
        message={`${
          data.redemption_type === "full" ? "Full" : "Partial"
        } Redemption`}
        description={
          data.redemption_type === "full"
            ? "Redeeming the entire redeemable amount."
            : `Partial redemption of GHS ${parseValue(
                redemption.redemption_amount,
              ).toFixed(2)} from total redeemable amount of GHS ${parseValue(
                data.redeemable_amount,
              ).toFixed(2)}.`
        }
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      {/* 1. Expected Initial Return */}
      <Divider orientation="left">
        <DollarOutlined style={{ marginRight: 8 }} />
        Expected Return at Maturity
      </Divider>

      <Alert
        message="Based on Initial Investment"
        description={`This shows the expected return if the full initial investment of GHS ${parseValue(
          data.initial_investment_amount,
        ).toFixed(2)} is held until maturity (${moment(
          data.maturity_date,
        ).format("DD MMM YYYY")} - ${data.tenure} days).`}
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Descriptions size="small" column={{ xs: 1, sm: 3}}>
        <Descriptions.Item
          label={<span style={{ fontWeight: 500 }}>Expected Interest</span>}
        >
          <span style={{ fontWeight: 600, fontSize: "16px" }}>
            GHS {parseValue(expected.expected_interest).toFixed(2)}
          </span>
        </Descriptions.Item>

        <Descriptions.Item
          label={<span style={{ fontWeight: 500 }}>Expected Total</span>}
        >
          <span style={{ fontWeight: 600, fontSize: "18px", color: "#1890ff" }}>
            GHS {parseValue(expected.expected_total_amount).toFixed(2)}
          </span>
        </Descriptions.Item>
      </Descriptions>

      {/* 2. Matured Redemption */}
      <Divider orientation="left" style={{ marginTop: 24 }}>
        <ClockCircleOutlined style={{ marginRight: 8 }} />
        Current Value at Maturity
      </Divider>

      <Alert
        message="Based on Current Redeemable Amount"
        description={`This shows what you would receive if you hold the current redeemable amount of GHS ${parseValue(
          data.redeemable_amount,
        ).toFixed(2)} until maturity.`}
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Descriptions size="small" column={{ xs: 1, sm: 3}}>
        <Descriptions.Item
          label={<span style={{ fontWeight: 500 }}>Interest Earned</span>}
        >
          <span style={{ fontWeight: 600, fontSize: "16px" }}>
            GHS {parseValue(matured.total_matured_interest).toFixed(2)}
          </span>
        </Descriptions.Item>

        <Descriptions.Item
          label={<span style={{ fontWeight: 500 }}>Total at Maturity</span>}
        >
          <span style={{ fontWeight: 600, fontSize: "18px", color: "#1890ff" }}>
            GHS {parseValue(matured.total_matured_amount).toFixed(2)}
          </span>
        </Descriptions.Item>
      </Descriptions>

      {/* 3. Redemption Calculation */}
      <Divider orientation="left" style={{ marginTop: 24 }}>
        <DollarOutlined style={{ marginRight: 8 }} />
        Redemption Calculation
      </Divider>

      {data.treasury_bill ? (
        <TreasuryBillRedemptionBreakdown data={data} />
      ) : (
        <>
          {isEarly ? (
            <Alert
              message="Early Redemption"
              description={`Redeeming on ${moment(
                redemption.rediscount_date,
              ).format("DD MMM YYYY")} (before maturity on ${moment(
                data.maturity_date,
              ).format(
                "DD MMM YYYY",
              )}). A 20% penalty will be applied to the interest earned.`}
              type="warning"
              icon={<WarningOutlined />}
              showIcon
              style={{ marginBottom: 16 }}
            />
          ) : (
            <Alert
              message="Matured Investment"
              description={`Redeeming on ${moment(
                redemption.rediscount_date,
              ).format(
                "DD MMM YYYY",
              )} (at or after maturity). Full interest earned with no penalties.`}
              type="success"
              icon={<CheckCircleOutlined />}
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          <Descriptions size="small" column={{ xs: 1, sm: 2, md: 3 }}>
            <Descriptions.Item
              label={<span style={{ fontWeight: 500 }}>Redemption Amount</span>}
            >
              <span style={{ fontWeight: 600, fontSize: "16px" }}>
                GHS {parseValue(redemption.redemption_amount).toFixed(2)}
              </span>
            </Descriptions.Item>

            <Descriptions.Item
              label={<span style={{ fontWeight: 500 }}>Interest Earned</span>}
            >
              <span style={{ fontWeight: 600, fontSize: "16px" }}>
                GHS{" "}
                {parseValue(redemption.redemption_interest_earned).toFixed(2)}
              </span>
            </Descriptions.Item>

            {isEarly && parseValue(redemption.redemption_interest_penalty) > 0 && (
              <Descriptions.Item
                label={<span style={{ fontWeight: 500 }}>Penalty (20%)</span>}
              >
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: "16px",
                    color: "#cf1322",
                  }}
                >
                  GHS{" "}
                  {parseValue(redemption.redemption_interest_penalty).toFixed(
                    2,
                  )}
                </span>
              </Descriptions.Item>
            )}

            <Descriptions.Item
              label={
                <span style={{ fontWeight: 500 }}>
                  Total Redemption{" "}
                  {data.redemption_type === "partial" && "(Partial)"}
                </span>
              }
              span={isEarly ? 2 : 3}
            >
              <span
                style={{ fontWeight: 600, fontSize: "18px", color: "#52c41a" }}
              >
                GHS {parseValue(redemption.total_redemption_amount).toFixed(2)}
              </span>
            </Descriptions.Item>

            {isEarly && parseValue(redemption.loss) > 0 && (
              <Descriptions.Item
                label={
                  <span style={{ fontWeight: 500 }}>Loss vs Maturity</span>
                }
              >
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: "16px",
                    color: "#fa8c16",
                  }}
                >
                  GHS {parseValue(redemption.loss).toFixed(2)}
                </span>
              </Descriptions.Item>
            )}

            <Descriptions.Item
              label={<span style={{ fontWeight: 500 }}>Rediscount Date</span>}
              span={isEarly ? 1 : 2}
            >
              {moment(redemption.rediscount_date).format("DD MMM YYYY")}
            </Descriptions.Item>
          </Descriptions>
        </>
      )}
    </div>
  );
};
